import React from "react";
import ReferenceGallery from "./ReferenceGallery";
import type { RestaurantDetails, Translate } from "../chatbotManagement.types";
import {
  formatCost,
  formatDate,
  formatNumber,
  formatSummaryCost,
  progressColor,
  usagePercentage,
} from "../chatbotManagement.utils";

interface ChatbotRestaurantDetailsModalProps {
  details: RestaurantDetails | null;
  onClose: () => void;
  t: Translate;
}

const ChatbotRestaurantDetailsModal = ({
  details,
  onClose,
  t,
}: ChatbotRestaurantDetailsModalProps): React.ReactElement | null => {
  if (!details) return null;

  return (
    <div
      className="modal d-block"
      role="dialog"
      aria-modal="true"
      aria-label={t("admin_chatbot_usage_details")}
      style={{ background: "rgba(0,0,0,.45)" }}
    >
      <div className="modal-dialog modal-lg" style={{ maxWidth: 760 }}>
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title">{details.restaurant_name}</h5>
              <small>{details.email}</small>
            </div>
            <button
              type="button"
              className="close"
              aria-label={t("chatbot_close")}
              onClick={onClose}
            >
              x
            </button>
          </div>
          <div className="modal-body">
            <ReferenceGallery restaurantId={details.restaurant_id} inline />
            <h6 className="mt-3">{t("admin_chatbot_lifetime_totals")}</h6>
            <div className="row mb-4">
              {[
                [t("admin_chatbot_tokens"), formatNumber(details.totals?.tokens_used)],
                [t("admin_chatbot_images"), formatNumber(details.totals?.image_generations_used)],
                [t("admin_chatbot_cost"), formatSummaryCost(details.totals?.cost_usd)],
              ].map(([label, value]) => (
                <div className="col-md-4 mb-2" key={label}>
                  <div className="border rounded p-3 h-100">
                    <div className="text-muted">{label}</div>
                    <div className="h4 mb-0">{value}</div>
                  </div>
                </div>
              ))}
            </div>
            <h6>{t("admin_chatbot_usage_windows")}</h6>
            {(details.usage_windows || []).map((window) => {
              const percentage = usagePercentage(window.tokens_used, window.token_limit);
              return (
                <div className="border rounded p-3 mb-3" key={window.key}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <strong className="text-capitalize h5 mb-0">{window.key}</strong>
                    <small className="text-muted">
                      {t("admin_chatbot_resets", {
                        date: formatDate(window.reset_at, t),
                      })}
                    </small>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <div
                      className="progress flex-grow-1 mr-2"
                      style={{ height: 10, background: "#ececec" }}
                    >
                      <div
                        className="progress-bar"
                        role="progressbar"
                        aria-label={`${window.key} token usage`}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={percentage}
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: progressColor(percentage),
                        }}
                      />
                    </div>
                    <strong>{percentage}%</strong>
                  </div>
                  <div>
                    {t("admin_chatbot_window_metrics", {
                      used: formatNumber(window.tokens_used),
                      limit: formatNumber(window.token_limit),
                      images: formatNumber(window.image_generations_used),
                      cost: formatCost(window.cost_usd),
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotRestaurantDetailsModal;
